import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Search, RefreshCw, Play, Pause, X, Mail, Clock, 
  CheckCircle, AlertCircle, User, Building, MoreHorizontal
} from 'lucide-react'
import toast from 'react-hot-toast'
import { getSequenceEnrollments, getSequenceEnrollmentStats, updateEnrollmentStatus, getEmailSequences } from '../../services/api'

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'var(--color-text-secondary)', bg: 'rgba(136,136,136,0.1)', icon: Clock },
  active: { label: 'Actif', color: '#5b5cf4', bg: 'rgba(91, 92, 244,0.1)', icon: Play },
  paused: { label: 'En pause', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: Pause },
  completed: { label: 'Terminé', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', icon: CheckCircle },
  exited: { label: 'Sorti', color: '#ef4444', bg: 'rgba(239,68,68,0.1)', icon: X },
  failed: { label: 'Échec', color: '#ef4444', bg: 'rgba(239,68,68,0.1)', icon: AlertCircle }
}

export default function SequenceProspectTracker() {
  const queryClient = useQueryClient()
  const [selectedSequence, setSelectedSequence] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [limit] = useState(50)
  const [selectedProspects, setSelectedProspects] = useState(new Set())
  const [expandedProspect, setExpandedProspect] = useState(null)
  const [showBulkActions, setShowBulkActions] = useState(false)

  // Fetch sequences
  const { data: sequencesData } = useQuery({
    queryKey: ['emailSequences'],
    queryFn: getEmailSequences
  })

  useEffect(() => {
    setPage(1)
    setSelectedProspects(new Set())
    setShowBulkActions(false)
  }, [selectedSequence, statusFilter, searchQuery])

  const sequences = sequencesData?.member || sequencesData?.['hydra:member'] || sequencesData || []

  const activeSequence = selectedSequence !== 'all'
    ? sequences.find(seq => String(seq.id) === String(selectedSequence))
    : null

  const { data: enrollmentsData, isLoading, refetch } = useQuery({
    queryKey: ['sequenceEnrollments', selectedSequence, statusFilter, searchQuery, page, limit],
    queryFn: async () => {
      if (!selectedSequence || selectedSequence === 'all') return null
      const options = {
        status: statusFilter === 'all' ? null : statusFilter,
        q: searchQuery || null,
        page,
        limit
      }
      const enrollments = await getSequenceEnrollments(selectedSequence, options)
      const enrollmentsList = enrollments?.items || enrollments?.enrollments || enrollments?.['hydra:member'] || enrollments?.member || []
      const totalSteps = (activeSequence?.workflowData?.nodes || []).filter(node => node.type === 'email').length
      return {
        ...enrollments,
        items: enrollmentsList.map(e => ({ ...e, sequenceName: activeSequence?.name, sequenceId: activeSequence?.id, totalSteps }))
      }
    },
    enabled: selectedSequence !== 'all' && !!selectedSequence
  })

  const { data: enrollmentStats } = useQuery({
    queryKey: ['sequenceEnrollmentStats', selectedSequence],
    queryFn: () => getSequenceEnrollmentStats(selectedSequence),
    enabled: selectedSequence !== 'all' && !!selectedSequence
  })

  // Update enrollment mutation
  const updateEnrollmentMutation = useMutation({
    mutationFn: ({ enrollmentId, status }) => updateEnrollmentStatus(enrollmentId, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['sequenceEnrollments'])
      queryClient.invalidateQueries(['sequenceEnrollmentStats'])
      toast.success('Prospect mis à jour')
    },
    onError: (err) => toast.error(err.message || 'Erreur mise à jour')
  })

  const enrollments = enrollmentsData?.items || []
  const enrollmentsTotal = enrollmentsData?.total || enrollments.length
  const enrollmentsHasMore = Boolean(enrollmentsData?.hasMore)

  const handleProspectSelect = (enrollmentId) => {
    setSelectedProspects(prev => {
      const newSet = new Set(prev)
      if (newSet.has(enrollmentId)) {
        newSet.delete(enrollmentId)
      } else {
        newSet.add(enrollmentId)
      }
      setShowBulkActions(newSet.size > 0)
      return newSet
    })
  }

  const handleBulkAction = async (action) => {
    const enrollmentIds = Array.from(selectedProspects)
    
    try {
      for (const enrollmentId of enrollmentIds) {
        await updateEnrollmentMutation.mutateAsync({ 
          enrollmentId, 
          status: action
        })
      }
      setSelectedProspects(new Set())
      setShowBulkActions(false)
      toast.success(`${enrollmentIds.length} prospect(s) ${action === 'paused' ? 'mis en pause' : 'mis à jour'}`)
    } catch (error) {
      toast.error('Erreur lors de l\'action groupée')
    }
  }

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending
    const Icon = config.icon
    
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        padding: '0.25rem 0.75rem',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: 500,
        color: config.color,
        background: config.bg
      }}>
        <Icon size={12} />
        {config.label}
      </div>
    )
  }

  const getProgressBar = (currentStep, totalSteps) => {
    const progress = totalSteps > 0 ? (currentStep / totalSteps) * 100 : 0
    
    return (
      <div style={{
        width: '100px',
        height: '6px',
        background: 'rgb(var(--rgb-ink) / 0.1)',
        borderRadius: '3px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${progress}%`,
          height: '100%',
          background: progress === 100 ? '#3b82f6' : '#5b5cf4',
          transition: 'width 0.3s ease'
        }} />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        color: 'var(--color-text-secondary)'
      }}>
        <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '0.5rem' }} />
        Chargement des données de tracking...
      </div>
    )
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-text)' }}>
          Tracking des Prospects
        </h1>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
          Vue granulaire de l'état de tous vos prospects dans les séquences actives
        </p>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        padding: '1rem',
        background: 'rgb(var(--rgb-ink) / 0.02)',
        borderRadius: '12px',
        border: '1px solid rgb(var(--rgb-ink) / 0.05)'
      }}>
        {/* Search */}
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={16} style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--color-text-secondary)'
          }} />
          <input
            type="text"
            placeholder="Rechercher par nom, email, entreprise..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 0.75rem 0.75rem 2.5rem',
              background: 'rgb(var(--rgb-ink) / 0.05)',
              border: '1px solid rgb(var(--rgb-ink) / 0.1)',
              borderRadius: '8px',
              color: 'var(--color-text)',
              fontSize: '0.9rem'
            }}
          />
        </div>

        {/* Sequence filter */}
        <select
          value={selectedSequence}
          onChange={(e) => setSelectedSequence(e.target.value)}
          style={{
            padding: '0.75rem',
            background: 'rgb(var(--rgb-ink) / 0.05)',
            border: '1px solid rgb(var(--rgb-ink) / 0.1)',
            borderRadius: '8px',
            color: 'var(--color-text)',
            fontSize: '0.9rem',
            minWidth: '200px'
          }}
        >
          <option value="all">Toutes les séquences</option>
          {sequences.map(seq => (
            <option key={seq.id} value={seq.id}>{seq.name}</option>
          ))}
        </select>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '0.75rem',
            background: 'rgb(var(--rgb-ink) / 0.05)',
            border: '1px solid rgb(var(--rgb-ink) / 0.1)',
            borderRadius: '8px',
            color: 'var(--color-text)',
            fontSize: '0.9rem',
            minWidth: '150px'
          }}
        >
          <option value="all">Tous les statuts</option>
          {Object.entries(STATUS_CONFIG).map(([status, config]) => (
            <option key={status} value={status}>{config.label}</option>
          ))}
        </select>

        {/* Refresh */}
        <button
          onClick={() => refetch()}
          disabled={selectedSequence === 'all'}
          style={{
            padding: '0.75rem',
            background: 'rgba(91, 92, 244,0.1)',
            border: '1px solid rgba(91, 92, 244,0.2)',
            borderRadius: '8px',
            color: '#5b5cf4',
            cursor: selectedSequence === 'all' ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <RefreshCw size={16} />
          Actualiser
        </button>
      </div>

      {/* Bulk actions */}
      {showBulkActions && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          background: 'rgba(91, 92, 244,0.05)',
          border: '1px solid rgba(91, 92, 244,0.2)',
          borderRadius: '8px'
        }}>
          <span style={{ color: '#5b5cf4', fontSize: '0.9rem', fontWeight: 500 }}>
            {selectedProspects.size} prospect(s) sélectionné(s)
          </span>
          <button
            onClick={() => handleBulkAction('paused')}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(245,158,11,0.1)',
              border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: '6px',
              color: '#f59e0b',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            Mettre en pause
          </button>
          <button
            onClick={() => handleBulkAction('exited')}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: '6px',
              color: '#ef4444',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            Sortir de la séquence
          </button>
        </div>
      )}

      {/* Stats summary */}
      {selectedSequence !== 'all' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          {Object.entries(STATUS_CONFIG).map(([status, config]) => {
            const count = enrollmentStats?.byStatus?.[status] ?? 0
            if (count === 0) return null
            return (
              <div key={status} style={{
                padding: '1rem',
                background: 'rgb(var(--rgb-ink) / 0.02)',
                borderRadius: '8px',
                border: `1px solid color-mix(in srgb, ${config.color} 13%, transparent)`
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  marginBottom: '0.5rem'
                }}>
                  <config.icon size={16} style={{ color: config.color }} />
                  <span style={{ color: 'var(--color-text)', fontWeight: 500 }}>{config.label}</span>
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 600, color: config.color }}>
                  {count}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Enrollments table */}
      <div style={{
        background: 'rgb(var(--rgb-ink) / 0.02)',
        borderRadius: '12px',
        border: '1px solid rgb(var(--rgb-ink) / 0.05)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid rgb(var(--rgb-ink) / 0.05)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <input
              type="checkbox"
              checked={selectedProspects.size === enrollments.length && enrollments.length > 0}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedProspects(new Set(enrollments.map(e => e.id)))
                  setShowBulkActions(true)
                } else {
                  setSelectedProspects(new Set())
                  setShowBulkActions(false)
                }
              }}
              style={{ accentColor: '#5b5cf4' }}
            />
            <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>
              {enrollmentsTotal} prospect(s)
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{
                padding: '0.35rem 0.75rem',
                background: 'rgb(var(--rgb-ink) / 0.05)',
                border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                borderRadius: '6px',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              ◀
            </button>
            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Page {page}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!enrollmentsHasMore}
              style={{
                padding: '0.35rem 0.75rem',
                background: 'rgb(var(--rgb-ink) / 0.05)',
                border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                borderRadius: '6px',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              ▶
            </button>
          </div>
        </div>

        <div>
          {selectedSequence === 'all' ? (
            <div style={{
              padding: '3rem',
              textAlign: 'center',
              color: 'var(--color-text-secondary)'
            }}>
              <User size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>Sélectionnez une séquence pour voir les prospects</p>
            </div>
          ) : enrollments.length === 0 ? (
            <div style={{
              padding: '3rem',
              textAlign: 'center',
              color: 'var(--color-text-secondary)'
            }}>
              <User size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>Aucun prospect trouvé avec ces filtres</p>
            </div>
          ) : (
            enrollments.map(enrollment => (
              <ProspectEnrollmentRow
                key={enrollment.id}
                enrollment={enrollment}
                isSelected={selectedProspects.has(enrollment.id)}
                onSelect={() => handleProspectSelect(enrollment.id)}
                isExpanded={expandedProspect === enrollment.id}
                onToggleExpand={() => setExpandedProspect(
                  expandedProspect === enrollment.id ? null : enrollment.id
                )}
                getStatusBadge={getStatusBadge}
                getProgressBar={getProgressBar}
                onUpdate={(status) => updateEnrollmentMutation.mutate({ 
                  enrollmentId: enrollment.id, 
                  status
                })}
              />
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// Composant pour chaque ligne de prospect
function ProspectEnrollmentRow({ 
  enrollment, 
  isSelected, 
  onSelect, 
  isExpanded, 
  onToggleExpand, 
  getStatusBadge,
  getProgressBar,
  onUpdate 
}) {
  const [showActions, setShowActions] = useState(false)
  const prospect = enrollment.prospect
  const recipient = enrollment.recipient
  const contactEmail = prospect?.primaryEmail || prospect?.email || recipient?.email || ''
  const contactName = prospect
    ? `${prospect?.firstName || ''} ${prospect?.lastName || ''}`.trim()
    : (recipient?.fullName || `${recipient?.firstName || ''} ${recipient?.lastName || ''}`.trim())
  const displayName = contactName || contactEmail || '—'
  const companyName = prospect?.companyName || prospect?.company || ''

  return (
    <div style={{
      borderBottom: '1px solid rgb(var(--rgb-ink) / 0.05)'
    }}>
      <div
        style={{
          padding: '1rem 1.5rem',
          display: 'grid',
          gridTemplateColumns: '40px 1fr 200px 150px 100px 120px 40px',
          gap: '1rem',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'background 0.2s',
          background: isSelected ? 'rgba(91, 92, 244,0.05)' : 'transparent'
        }}
        onClick={onToggleExpand}
        onMouseEnter={(e) => e.currentTarget.style.background = isSelected ? 'rgba(91, 92, 244,0.08)' : 'rgb(var(--rgb-ink) / 0.02)'}
        onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? 'rgba(91, 92, 244,0.05)' : 'transparent'}
      >
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation()
            onSelect()
          }}
          style={{ accentColor: '#5b5cf4' }}
        />

        {/* Prospect info */}
        <div>
          <div style={{ fontWeight: 500, color: 'var(--color-text)', marginBottom: '0.25rem' }}>
            {displayName}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
            {contactEmail}
          </div>
          {companyName && (
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }}>
              <Building size={10} />
              {companyName}
            </div>
          )}
        </div>

        {/* Sequence */}
        <div style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
          {enrollment.sequenceName}
        </div>

        {/* Status */}
        <div>
          {getStatusBadge(enrollment.status)}
        </div>

        {/* Progress */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
            Étape {enrollment.currentStep || 0}
          </div>
          {getProgressBar(enrollment.currentStep || 0, enrollment.totalSteps || 1)}
        </div>

        {/* Emails sent */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', justifyContent: 'center' }}>
            <Mail size={12} style={{ color: 'var(--color-text-secondary)' }} />
            <span style={{ fontSize: '0.9rem', color: 'var(--color-text)' }}>
              {enrollment.emailsSent || 0}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShowActions(!showActions)
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <MoreHorizontal size={16} />
          </button>

          {showActions && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              background: 'rgb(var(--rgb-surface-hover))',
              border: '1px solid rgb(var(--rgb-ink) / 0.1)',
              borderRadius: '8px',
              padding: '0.5rem',
              minWidth: '160px',
              zIndex: 100,
              boxShadow: '0 4px 12px rgb(var(--rgb-shadow) / calc(0.5 * var(--shadow-k)))'
            }}>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onUpdate('paused')
                  setShowActions(false)
                }}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: 'none',
                  padding: '0.5rem 0.75rem',
                  color: '#f59e0b',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.85rem'
                }}
              >
                <Pause size={14} /> Mettre en pause
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onUpdate('active')
                  setShowActions(false)
                }}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: 'none',
                  padding: '0.5rem 0.75rem',
                  color: '#5b5cf4',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.85rem'
                }}
              >
                <Play size={14} /> Réactiver
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onUpdate('exited')
                  setShowActions(false)
                }}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: 'none',
                  padding: '0.5rem 0.75rem',
                  color: '#ef4444',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.85rem'
                }}
              >
                <X size={14} /> Sortir de la séquence
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div style={{
          padding: '1rem 1.5rem',
          background: 'rgb(var(--rgb-ink) / 0.02)',
          borderTop: '1px solid rgb(var(--rgb-ink) / 0.05)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '2rem'
          }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--color-text)' }}>
                Détails de progression
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                <div style={{ padding: '0.75rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: '0.35rem' }}>Inscrit le</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-text)' }}>
                    {enrollment.enrolledAt ? new Date(enrollment.enrolledAt).toLocaleString('fr-FR') : '—'}
                  </div>
                </div>
                <div style={{ padding: '0.75rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: '0.35rem' }}>Prochaine étape</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-text)' }}>
                    {enrollment.nextStepAt ? new Date(enrollment.nextStepAt).toLocaleString('fr-FR') : '—'}
                  </div>
                </div>
                <div style={{ padding: '0.75rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: '0.35rem' }}>Emails ouverts</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-text)' }}>{enrollment.emailsOpened || 0}</div>
                </div>
                <div style={{ padding: '0.75rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: '0.35rem' }}>Emails cliqués</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-text)' }}>{enrollment.emailsClicked || 0}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
